import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/global.css';

const DocViewer = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const docUrl = queryParams.get('url');
    const docTitle = queryParams.get('title') || 'Documento de Estudio';

    const [selectedText, setSelectedText] = useState('');
    const [showTools, setShowTools] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const handleSelection = () => {
            const selection = window.getSelection().toString();
            if (selection && selection.length > 5) {
                setSelectedText(selection);
                setShowTools(true);
            } else {
                setShowTools(false);
            }
        };

        document.addEventListener('mouseup', handleSelection);
        return () => document.removeEventListener('mouseup', handleSelection);
    }, []);

    if (!docUrl) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <h2>No se especificó ningún documento</h2>
                <button onClick={() => window.history.back()} className="btn-accent">Volver</button>
            </div>
        );
    }

    const sendToIA = () => {
        // Navegar a IA con el texto pre-cargado (podríamos usar state o query)
        navigate('/dashboard/ia-vortex', { state: { initialPrompt: `Explícame este fragmento de mi material de estudio: "${selectedText}"` } });
    };

    const sendToTTS = () => {
        navigate('/dashboard/vortex-voices', { state: { initialText: selectedText } });
    };

    const downloadDoc = () => {
        const link = document.createElement('a');
        link.href = docUrl;
        link.download = `${docTitle.replace(/\s+/g, '_')}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Transformar URLs de Dropbox para máxima compatibilidad
    const getTransformedUrl = (url) => {
        if (!url) return '';
        if (url.includes('dropbox.com')) {
            // Convertir a enlace de contenido directo para saltar visualizadores intermedios
            return url.replace('www.dropbox.com', 'dl.dropboxusercontent.com').replace('?dl=0', '').replace('&raw=1', '');
        }
        return url;
    };

    const transformedUrl = getTransformedUrl(docUrl);
    const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(transformedUrl)}&embedded=true`;

    const handleLoad = () => {
        setIsLoading(false);
    };

    return (
        <div className="doc-viewer-container" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)', color: 'var(--text-primary)' }}>
            <div className="viewer-header" style={{
                padding: '15px 25px',
                background: 'var(--bg-secondary)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid rgba(255,255,255,0.1)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <button onClick={() => window.history.back()} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1.2rem' }}>←</button>
                    <h2 style={{ margin: 0, fontSize: '1.2rem' }}>{docTitle}</h2>
                </div>
                <div className="viewer-actions" style={{ display: 'flex', gap: '10px' }}>
                    <button
                        onClick={downloadDoc}
                        className="btn-secondary"
                        style={{ padding: '5px 15px', borderRadius: '50px', fontSize: '0.8rem', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
                    >
                        ⬇️ Descargar
                    </button>
                    {showTools && (
                        <div className="selection-tools" style={{
                            display: 'flex',
                            gap: '10px',
                            background: 'var(--accent)',
                            padding: '5px 15px',
                            borderRadius: '50px',
                            animation: 'fadeIn 0.2s ease'
                        }}>
                            <span style={{ fontSize: '0.8rem', alignSelf: 'center', fontWeight: 'bold' }}>¿Qué quieres hacer con el texto seleccionado?</span>
                            <button onClick={sendToIA} style={{ background: 'white', color: 'var(--accent)', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold' }}>🤖 Enviar a IA</button>
                            <button onClick={sendToTTS} style={{ background: 'white', color: 'var(--accent)', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold' }}>🔊 Escuchar</button>
                        </div>
                    )}
                </div>
            </div>

            <div className="iframe-wrapper" style={{ flex: 1, position: 'relative', background: '#222', overflow: 'hidden' }}>
                {isLoading && (
                    <div className="viewer-loading" style={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        background: 'var(--bg-primary)',
                        zIndex: 10
                    }}>
                        <div className="spinner" style={{
                            width: '40px',
                            height: '40px',
                            border: '4px solid rgba(255,255,255,0.1)',
                            borderTop: '4px solid var(--accent)',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            marginBottom: '15px'
                        }}></div>
                        <p style={{ opacity: 0.7, fontSize: '0.9rem' }}>Cargando documento técnico...</p>
                    </div>
                )}

                <iframe
                    src={viewerUrl}
                    title={docTitle}
                    style={{ width: '100%', height: '100%', border: 'none', opacity: isLoading ? 0 : 1, transition: 'opacity 0.5s ease' }}
                    onLoad={handleLoad}
                />

                {/* Overlay informativo */}
                {!isLoading && (
                    <div style={{
                        position: 'absolute',
                        bottom: '20px',
                        right: '20px',
                        background: 'rgba(0,0,0,0.7)',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontSize: '0.8rem',
                        pointerEvents: 'none',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }}>
                        💡 Selecciona texto para IA o Audio.
                    </div>
                )}
            </div>

            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default DocViewer;
